/* eslint-disable i18next/no-literal-string */
import {Badge} from '@dropbox/dig-components/badge';
import {Button} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {snackbarAtom} from 'atoms/snackbar';
import {
  AdminTimeframesService,
  type TimeframeAssignmentApplyResult,
  type TimeframeAssignmentEdge,
  type TimeframeAssignmentRow,
  type TimeframeAssignmentTimeframe,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue, useSetAtom} from 'jotai';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router';
import {getBackendUrl, getService} from 'utilities';

import styles from './AdminTimeframeAssignment.module.css';
import {errorMessage} from './AdminTimeframes';

export const timeframeAssignmentQueryKey = ['admin', 'timeframes', 'assignment'] as const;
const verifyQueryKey = [...timeframeAssignmentQueryKey, 'verify'] as const;

const service = () => getService(AdminTimeframesService);

// The no-root and one-root lists can run to thousands; the CSV has every row.
const MAX_ROWS = 200;

type TabId = 'several' | 'pillars' | 'crossing' | 'none' | 'one';

const workstreamColumns: Column<TimeframeAssignmentRow>[] = [
  {type: 'Workstream', minWidth: 240},
  {type: 'Reached from', minWidth: 200},
  {type: 'Gets', fixed: true, minWidth: 110},
  {type: 'Paths kept (breadcrumbs)', minWidth: 320},
];

const edgeColumns: Column<TimeframeAssignmentEdge>[] = [
  {type: 'Parent', minWidth: 240},
  {type: 'Parent gets', fixed: true, minWidth: 110},
  {type: 'Child', minWidth: 240},
  {type: 'Child gets', fixed: true, minWidth: 110},
];

const formatCount = (count: number) => count.toLocaleString('en-US');

const TimeframeBadge = ({timeframe}: {timeframe?: TimeframeAssignmentTimeframe | null}) =>
  timeframe ? (
    <Badge variant="attention">{timeframe.name}</Badge>
  ) : (
    <Text color="subtle">No timeframe</Text>
  );

const WorkstreamName = ({id, name, isPillar}: {id: number; name: string; isPillar?: boolean}) => (
  <Stack gap="4">
    <Link to={`/workstreams/${id}`} className={styles.name}>
      <Text isBold>{name}</Text>
    </Link>
    <Text size="small" color="subtle">
      #{id}
      {isPillar ? ' · pillar' : ''}
    </Text>
  </Stack>
);

const pathsKept = (row: TimeframeAssignmentRow) => {
  if (row.is_pillar && (row.paths ?? []).length > 1 && row.inferred_timeframe) {
    const earlier = row.reached_from
      .filter((timeframe) => timeframe.id !== row.inferred_timeframe?.id)
      .map((timeframe) => timeframe.name)
      .join(' and ');
    return (
      <Text color="subtle">
        Pillar in {row.inferred_timeframe.name} only. Leaves the {earlier} sidebar
        {row.reached_from.length > 2 ? 's' : ''}.
      </Text>
    );
  }
  return (
    <Stack gap="4">
      {(row.paths ?? []).map((path) => (
        <Text key={path.timeframe.id} color="subtle">
          {[path.timeframe.name, ...path.nodes.slice(0, -1).map((node) => node.name), 'here'].join(
            ' › '
          )}
        </Text>
      ))}
    </Stack>
  );
};

const WorkstreamTable = ({rows, testId}: {rows: TimeframeAssignmentRow[]; testId: string}) => (
  <Stack gap="8">
    <Table columns={workstreamColumns} data={rows.slice(0, MAX_ROWS)} compact>
      <Table.Header />
      <Table.Body>
        {rows.length ? (
          rows.slice(0, MAX_ROWS).map((row) => (
            <Table.Row key={row.workstream_id} data-testid={`${testId}-${row.workstream_id}`}>
              <Table.Cell>
                <WorkstreamName id={row.workstream_id} name={row.name} isPillar={row.is_pillar} />
              </Table.Cell>
              <Table.Cell>
                <div className={styles.badges}>
                  {row.reached_from.length ? (
                    row.reached_from.map((timeframe) => (
                      <Badge key={timeframe.id} variant="standard">
                        {timeframe.name}
                      </Badge>
                    ))
                  ) : (
                    <Text color="subtle">No root</Text>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <TimeframeBadge timeframe={row.inferred_timeframe} />
              </Table.Cell>
              <Table.Cell>{pathsKept(row)}</Table.Cell>
            </Table.Row>
          ))
        ) : (
          <Table.Row>
            <Table.Cell colSpan={workstreamColumns.length}>
              <Box paddingY="16">
                <Text color="subtle">None.</Text>
              </Box>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
    {rows.length > MAX_ROWS ? (
      <Text size="small" color="subtle">
        Showing the first {MAX_ROWS} of {formatCount(rows.length)}. Download the CSV for all of
        them.
      </Text>
    ) : null}
  </Stack>
);

const EdgeTable = ({edges}: {edges: TimeframeAssignmentEdge[]}) => (
  <Table columns={edgeColumns} data={edges} compact>
    <Table.Header />
    <Table.Body>
      {edges.length ? (
        edges.map((edge) => (
          <Table.Row
            key={`${edge.parent.id}-${edge.child.id}`}
            data-testid={`crossing-${edge.parent.id}-${edge.child.id}`}
          >
            <Table.Cell>
              <WorkstreamName id={edge.parent.id} name={edge.parent.name} />
            </Table.Cell>
            <Table.Cell>
              <TimeframeBadge timeframe={edge.parent_timeframe} />
            </Table.Cell>
            <Table.Cell>
              <WorkstreamName id={edge.child.id} name={edge.child.name} />
            </Table.Cell>
            <Table.Cell>
              <TimeframeBadge timeframe={edge.child_timeframe} />
            </Table.Cell>
          </Table.Row>
        ))
      ) : (
        <Table.Row>
          <Table.Cell colSpan={edgeColumns.length}>
            <Box paddingY="16">
              <Text color="subtle">None.</Text>
            </Box>
          </Table.Cell>
        </Table.Row>
      )}
    </Table.Body>
  </Table>
);

const downloadCsv = async () => {
  const response = await fetch(`${getBackendUrl()}/api/v1/admin/timeframes/assignment/csv`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  const filename =
    /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ??
    'CompanyOS-Timeframe-Assignment.csv';
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ApplyModal = ({
  open,
  toSetCount,
  onClose,
  onApplied,
}: {
  open: boolean;
  toSetCount: number;
  onClose: () => void;
  onApplied: (result: TimeframeAssignmentApplyResult) => void;
}) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => service().applyTimeframeAssignmentApiV1AdminTimeframesAssignmentApplyPost({}),
    onSuccess: async (result) => {
      onApplied(result);
      await queryClient.invalidateQueries({queryKey: timeframeAssignmentQueryKey});
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      withCloseButton="close"
      aria-label="Set timeframes"
      onRequestClose={() => {
        if (!mutation.isPending) onClose();
      }}
    >
      <Modal.Header>
        <Text size="large" isBold>
          Set {formatCount(toSetCount)} timeframe{toSetCount === 1 ? '' : 's'}?
        </Text>
      </Modal.Header>
      <Modal.Body>
        <Stack gap="12">
          <Text>
            Each workstream gets the timeframe shown in this report. It runs in batches of 200,
            committing each one.
          </Text>
          <Text color="subtle">
            Workstreams that already have a timeframe are never changed. Every change is in the
            workstream audit log under its batch ID, so a batch can be reverted.
          </Text>
          {mutation.isError ? (
            <Text role="alert" color="error">
              {errorMessage(mutation.error)}
            </Text>
          ) : null}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="opacity" disabled={mutation.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
          Set timeframes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const VerifyPanel = () => {
  const {data, isLoading, isError} = useQuery({
    queryKey: verifyQueryKey,
    queryFn: () => service().verifyTimeframeAssignmentApiV1AdminTimeframesAssignmentVerifyGet(),
  });
  if (isLoading) return null;
  if (isError || !data) {
    return (
      <SystemBanner tone="alert" role="alert">
        <Text>Unable to check the assigned timeframes. Try refreshing the page.</Text>
      </SystemBanner>
    );
  }
  if (!data.mismatch_count) {
    return (
      <SystemBanner tone="success" role="status">
        <Text>
          Verified: all {formatCount(data.checked_count)} workstreams under a root have their latest
          root’s timeframe.
        </Text>
      </SystemBanner>
    );
  }
  const setByHand = data.mismatches.filter((mismatch) => mismatch.actual_timeframe);
  return (
    <SystemBanner tone="attention" role="status">
      <Stack gap="4">
        <Text>
          {formatCount(data.mismatch_count)} of {formatCount(data.checked_count)} workstreams under
          a root don’t have their latest root’s timeframe yet.
        </Text>
        {setByHand.length ? (
          <Text size="small" color="subtle">
            Already in another timeframe, so the backfill leaves them:{' '}
            {setByHand
              .slice(0, 10)
              .map(
                (mismatch) =>
                  `${mismatch.workstream.name} (#${mismatch.workstream.id}) is in ${mismatch.actual_timeframe?.name}, not ${mismatch.expected_timeframe.name}`
              )
              .join('; ')}
            .
          </Text>
        ) : null}
      </Stack>
    </SystemBanner>
  );
};

const StatCard = ({count, label}: {count: number; label: string}) => (
  <div className={styles.stat}>
    <Text className={styles.statCount}>{formatCount(count)}</Text>
    <Text color="subtle">{label}</Text>
  </div>
);

export const AdminTimeframeAssignment = () => {
  useDocumentTitle('Timeframe assignment');
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const canView = Boolean(pulseUser && isUserAdmin(pulseUser));
  const [selectedTab, setSelectedTab] = useState<TabId>('several');
  const [downloading, setDownloading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [lastRun, setLastRun] = useState<TimeframeAssignmentApplyResult | null>(null);

  const {data, isLoading, isError} = useQuery({
    queryKey: timeframeAssignmentQueryKey,
    queryFn: () => service().getTimeframeAssignmentReportApiV1AdminTimeframesAssignmentGet(),
    enabled: canView,
  });

  if (!pulseUser) return null;
  if (!canView) {
    navigate('/');
    return null;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadCsv();
    } catch {
      setSnackbarMessage({text: 'Unable to download the CSV. Try again.'});
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Timeframes', to: ROUTE_PATHS.ADMIN_TIMEFRAMES},
        {children: 'Timeframe assignment', to: ROUTE_PATHS.ADMIN_TIMEFRAME_ASSIGNMENT},
      ]}
      width="100%"
    >
      <Stack gap="24">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{gap: 16}}
        >
          <Stack gap="8">
            <Title size={24}>Timeframe assignment</Title>
            <Text color="subtle">
              Each workstream gets the timeframe of the latest legacy narrative root it sits under.
              {data
                ? ` The backfill sets ${formatCount(data.to_set_count)} timeframe${
                    data.to_set_count === 1 ? '' : 's'
                  }, and never changes one that’s already set.`
                : ''}
            </Text>
          </Stack>
          <Box display="flex" style={{gap: 8}}>
            <Button variant="outline" disabled={!data || downloading} onClick={handleDownload}>
              Download CSV
            </Button>
            <Button
              variant="primary"
              disabled={!data?.to_set_count}
              onClick={() => setApplyOpen(true)}
            >
              Set timeframes
            </Button>
          </Box>
        </Box>

        {lastRun ? (
          <SystemBanner tone="success" role="status">
            <Text>
              Set {formatCount(lastRun.set_count)} timeframe{lastRun.set_count === 1 ? '' : 's'} in{' '}
              {lastRun.batches.length} batch{lastRun.batches.length === 1 ? '' : 'es'}
              {lastRun.batches.length
                ? ` (${lastRun.batches.map((batch) => batch.batch_id).join(', ')})`
                : ''}
              .{lastRun.remaining_count ? ` ${formatCount(lastRun.remaining_count)} left.` : ''}
            </Text>
          </SystemBanner>
        ) : null}

        <VerifyPanel />

        {isError ? (
          <SystemBanner tone="alert" role="alert">
            <Text>Unable to load the assignment report. Try refreshing the page.</Text>
          </SystemBanner>
        ) : null}

        {isLoading ? (
          <Box display="flex" alignItems="center" style={{gap: 8}}>
            <Spinner size="small" />
            <Text color="subtle">Inferring timeframes</Text>
          </Box>
        ) : null}

        {data ? (
          <>
            <div className={styles.stats}>
              <StatCard count={data.one_root_count} label="Under one root" />
              <StatCard
                count={data.several_roots_count}
                label="Under several roots: latest root’s timeframe"
              />
              <StatCard
                count={data.crossing_edge_count}
                label="Existing edges that will cross timeframes, kept"
              />
              <StatCard count={data.no_root_count} label="Under no root: no timeframe" />
            </div>

            <Tabs selectedTab={selectedTab} onSelection={(tab) => setSelectedTab(tab as TabId)}>
              <Tabs.Group hasHorizontalPadding={false}>
                <Tabs.Tab id="several">
                  Under several roots ({formatCount(data.several_roots_count)})
                </Tabs.Tab>
                <Tabs.Tab id="pillars">
                  Pillars under several roots (
                  {formatCount(data.pillars_under_several_roots.length)})
                </Tabs.Tab>
                <Tabs.Tab id="crossing">
                  Crossing edges ({formatCount(data.crossing_edge_count)})
                </Tabs.Tab>
                <Tabs.Tab id="none">No root ({formatCount(data.no_root_count)})</Tabs.Tab>
                <Tabs.Tab id="one">One root ({formatCount(data.one_root_count)})</Tabs.Tab>
              </Tabs.Group>
              <Tabs.Panel tabId="several">
                <Box paddingTop="16">
                  {selectedTab === 'several' ? (
                    <WorkstreamTable rows={data.several_roots} testId="several" />
                  ) : null}
                </Box>
              </Tabs.Panel>
              <Tabs.Panel tabId="pillars">
                <Box paddingTop="16">
                  {selectedTab === 'pillars' ? (
                    <WorkstreamTable rows={data.pillars_under_several_roots} testId="pillar" />
                  ) : null}
                </Box>
              </Tabs.Panel>
              <Tabs.Panel tabId="crossing">
                <Box paddingTop="16">
                  {selectedTab === 'crossing' ? <EdgeTable edges={data.crossing_edges} /> : null}
                </Box>
              </Tabs.Panel>
              <Tabs.Panel tabId="none">
                <Box paddingTop="16">
                  {selectedTab === 'none' ? (
                    <WorkstreamTable rows={data.no_root} testId="none" />
                  ) : null}
                </Box>
              </Tabs.Panel>
              <Tabs.Panel tabId="one">
                <Box paddingTop="16">
                  {selectedTab === 'one' ? (
                    <WorkstreamTable rows={data.one_root} testId="one" />
                  ) : null}
                </Box>
              </Tabs.Panel>
            </Tabs>

            <Text size="small" color="subtle">
              Existing edges to earlier halves are kept, so these workstreams still appear under
              every parent listed. Only new edges have to stay within one timeframe.
              {data.archived_count
                ? ` ${formatCount(data.archived_count)} archived workstreams keep no timeframe and aren’t listed.`
                : ''}
            </Text>
          </>
        ) : null}
      </Stack>

      <ApplyModal
        open={applyOpen}
        toSetCount={data?.to_set_count ?? 0}
        onClose={() => setApplyOpen(false)}
        onApplied={setLastRun}
      />
    </Layout.Container>
  );
};
